/**
 * IncomingCallBanner — top-right toast for a call ringing the agent's own
 * line. Visual port of the demo repo's IncomingCallToast (PhoneCallLayer),
 * adapted to the production palette and the real twilio.context state.
 *
 * Keys off `incomingCall` (NOT `state === "incoming"`): a terminal screen
 * ("ended"/"error") keeps the state machine, but the ring must still surface.
 * No local timeout either — the backend's 20s dial timeout fires the ring's
 * `cancel`, which clears `incomingCall` and animates the banner away.
 */
import { AnimatePresence, motion } from "framer-motion";
import { Phone, PhoneIncoming, PhoneOff, User } from "lucide-react";
import { useTwilioActions, useTwilioState } from "@/context/twilio.context";
import { formatE164 } from "@/models/phone-number.model";
import { getLeadDetailsUrl } from "@/routes/paths";
import { getInitials } from "@/utils/strings";

const IncomingCallBanner = () => {
  const { incomingCall } = useTwilioState();
  const { acceptIncoming, rejectIncoming } = useTwilioActions();

  const callerNumber = incomingCall?.fromE164 ? formatE164(incomingCall.fromE164) : null;
  const title = incomingCall?.leadName ?? callerNumber ?? "Unknown caller";
  // Second line: the number under a known lead; "Unknown caller" under a bare
  // number; nothing when there is neither (the title already says it all).
  const caption = incomingCall?.leadName
    ? callerNumber ?? "No caller ID"
    : callerNumber
      ? "Unknown caller"
      : null;

  return (
    <AnimatePresence>
      {incomingCall && (
        <motion.div
          role="alert"
          aria-label="Incoming call"
          initial={{ opacity: 0, y: -16, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -16, scale: 0.97 }}
          transition={{ type: "spring", stiffness: 380, damping: 30 }}
          className="fixed right-5 top-5 z-[200] w-[340px] rounded-2xl border border-white/10 bg-gradient-to-b from-[#1B1F28]/95 to-[#0D1018]/95 p-4 shadow-[0_10px_40px_rgba(0,0,0,0.5)] backdrop-blur-md"
        >
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent-green">
            <span className="relative grid h-5 w-5 place-items-center">
              <span className="absolute inset-0 animate-ping rounded-full bg-accent-green/25" />
              <PhoneIncoming className="relative h-3.5 w-3.5" />
            </span>
            Incoming call
          </div>

          <div className="mt-3 flex items-center gap-3">
            {incomingCall.leadName ? (
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 text-[15px] font-semibold text-white ring-1 ring-white/10">
                {getInitials(incomingCall.leadName)}
              </div>
            ) : (
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-white/[0.05] ring-1 ring-white/10">
                <User className="h-5 w-5 text-slate-400" />
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate text-[15px] font-semibold text-white">{title}</p>
              {caption && <p className="truncate text-[12px] text-slate-400">{caption}</p>}
              {incomingCall.leadId !== null && (
                <a
                  href={getLeadDetailsUrl(incomingCall.leadId)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11.5px] font-medium text-brand-300 transition hover:text-brand-200"
                >
                  View lead →
                </a>
              )}
            </div>
          </div>

          {incomingCall.toE164 && (
            <p className="mt-2.5 text-[11px] text-slate-500">
              To your line · {formatE164(incomingCall.toE164)}
            </p>
          )}

          <div className="mt-3.5 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={rejectIncoming}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-accent-red/30 bg-accent-red/10 text-[13px] font-semibold text-red-300 transition hover:bg-accent-red/20"
            >
              <PhoneOff className="h-4 w-4" />
              Decline
            </button>
            <button
              type="button"
              onClick={acceptIncoming}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-accent-green text-[13px] font-semibold text-[#0D1018] shadow-[0_0_24px_-6px_rgba(34,197,94,0.8)] transition hover:brightness-110"
            >
              <Phone className="h-4 w-4" />
              Accept
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default IncomingCallBanner;
